// src/components/atoms/ActionButton.js
import React, { cloneElement } from "react";
import { Button, useTheme } from "@mui/material";

const ActionButton = ({
  icon,
  text,
  onClick,
  sx,
  iconPosition = "left", // default to left for backwards compatibility
  iconWidth = 24, // default icon width is 24px
  iconHeight = 24, // default icon height is 24px
  inputProps, // optional props for a hidden input field
  ...rest
}) => {
  const theme = useTheme();
  // Resolve sx if it’s a function.
  const resolvedSx = typeof sx === "function" ? sx(theme) : sx;

  // If inputProps is provided, default to component="label" unless a valid component is passed.
  const computedComponent = inputProps
    ? typeof rest.component === "boolean" || !rest.component
      ? "label"
      : rest.component
    : rest.component;

  // Helper to render the icon with proper margin based on iconPosition.
  const renderIcon = () => {
    if (!icon) return null;
    const marginStyle =
      iconPosition === "left"
        ? { marginRight: text ? 8 : 0 }
        : { marginLeft: text ? 8 : 0 };

    if (typeof icon === "string") {
      return (
        <img
          src={icon}
          alt=""
          style={{
            width: iconWidth,
            height: iconHeight,
            ...marginStyle,
            objectFit: "contain",
          }}
        />
      );
    } else if (React.isValidElement(icon)) {
      return cloneElement(icon, {
        sx: { width: iconWidth, height: iconHeight, ...marginStyle },
      });
    } else if (typeof icon === "function") {
      const IconComponent = icon;
      return (
        <IconComponent
          sx={{
            width: iconWidth,
            height: iconHeight,
            ...marginStyle,
          }}
        />
      );
    } else {
      return null;
    }
  };

  return (
    <Button
      variant="contained"
      onClick={onClick}
      component={computedComponent}
      sx={{
        flex: 1,
        borderRadius: "50px",
        textTransform: "none",
        ...resolvedSx,
      }}
      {...rest}
    >
      {iconPosition === "left" && renderIcon()}
      {text && text}
      {iconPosition === "right" && renderIcon()}
      {inputProps && <input hidden {...inputProps} />}
    </Button>
  );
};

export default ActionButton;
